import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { observer } from 'mobx-react';
import {
  Avatar,
  Badge,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import AuthStore from '../../store/AuthStore';
import DocumentStore from '../../store/DocumentStore';
import { Close, CloudUpload, DeleteForever, FilePresentTwoTone } from '@mui/icons-material';
import { CustomStyledDialog } from '../../utils/helper';
import { Document, Page } from 'react-pdf';
import LoadingProgressStore from '../../store/LoadingProgressStore';
import { severity } from '../../utils/severity';
import SnackbarStore from '../../store/SnackbarStore';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const CustomUploadButton = styled(Button)({
  backgroundColor: '#F07B0E',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#f1785a',
  },
  width: 'fit-content',
});

const CustomBadge = styled(Badge)({
  '& .MuiBadge-badge': {
    top: '8px',
    right: '10px',
  },
});

const SmallAvatar = styled(Avatar)(({ theme }) => ({
  width: 24,
  height: 24,
  border: `2px solid ${theme.palette.background.paper}`,
  backgroundColor: '#d32f2f',
}));

export const UploadLeadDocDialog = observer(
  ({
    leadDetails,
    uploadFilesDocumentType,
    toggleUploadLeadDocDialogDialog,
    handleToggleUploadLeadDocDialogDialog,
  }) => {
    const [files, setFiles] = useState([]);

    const handleFileInputChange = (e) => {
      LoadingProgressStore.toggleIsLoading(true);
      const fileInfo = e.target.files[0];
      // currentFileInfo = fileInfo
      // if (fileInfo) {
      //   const reader = new FileReader();
      //   reader.onloadend = (event) => {
      //     setFiles([...files, {id: Math.random(), isNew: true, name: currentFileInfo.name, size: currentFileInfo.size, type: currentFileInfo.type, file: fileInfo, fileBinary: event.target.result}])
      //     currentFileInfo = null
      //   };
      //   // reader.readAsDataURL(fileInfo);
      //   reader.readAsBinaryString(fileInfo);
      // }
      if (fileInfo.size > 30000000) {
        SnackbarStore.toggleSnackbar(true, severity.error, 'File size is too large, The max file size is 30 MB ');
        LoadingProgressStore.toggleIsLoading(false);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = async (event) => {
        let base64Image = reader.result;
        if (fileInfo.type.includes('pdf')) {
          base64Image = await loadThumbnail(reader.result);
        }

        setFiles([
          ...files,
          {
            id: Math.random(),
            isNew: true,
            name: fileInfo.name,
            size: fileInfo.size,
            type: fileInfo.type,
            file: fileInfo,
            fileBinary: event.target.result,
            base64Image,
          },
        ]);

        LoadingProgressStore.toggleIsLoading(false);
      };
      reader.readAsDataURL(fileInfo);
      // setFiles([...files, {id: Math.random(), isNew: true, name: fileInfo.name, size: fileInfo.size, type: fileInfo.type, file: fileInfo}])
    };

    const loadThumbnail = async (data) => {
      const loadingTask = window.pdfjsLib.getDocument(data);
      const pdf = await loadingTask.promise;

      const page = await pdf.getPage(1); // Get the first page
      const viewport = page.getViewport({ scale: 1.5 });
      // viewport.height = '100px'
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = '600';
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;

      // Convert canvas content to data URL (image)
      return canvas.toDataURL('image/png');
    };

    const handleRemoveDocument = (fileId) => {
      setFiles(files.filter((item) => item.id.toString() !== fileId.toString()));
    };

    const handleSubmit = (e) => {
      e.preventDefault();

      const payload = {
        leadId: leadDetails.id,
        userId: AuthStore.userDetails.Id,
        files: [],
        documentType: uploadFilesDocumentType,
      };

      const formData = new FormData();
      files.forEach((item) => {
        formData.append('Files', item.file);
      });
      payload.files = formData;

      DocumentStore.uploadDocumentToS3(payload);
    };

    useEffect(() => {
      toggleUploadLeadDocDialogDialog && setFiles([]);
    }, [toggleUploadLeadDocDialogDialog]);

    useEffect(() => {
      DocumentStore.uploadDocumentStatus && handleToggleUploadLeadDocDialogDialog();
    }, [DocumentStore.uploadDocumentStatus]);

    const isEmpty = (value) => {
      return value === null || value === undefined || (!Number.isInteger(value) && value?.trim() === '');
    };

    return (
      <CustomStyledDialog
        open={toggleUploadLeadDocDialogDialog}
        onClose={(event, reason) => reason !== 'backdropClick' && handleToggleUploadLeadDocDialogDialog()}
        fullWidth={true}
        maxWidth={'xs'}
        noValidate
        PaperProps={{
          component: 'form',
          noValidate: true,
          onSubmit: (e) => handleSubmit(e),
        }}
      >
        <DialogTitle>
          <Stack>
            <Typography sx={{ fontWeight: '700' }} component="p">
              Lead Supporting Documents
            </Typography>
          </Stack>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleToggleUploadLeadDocDialogDialog}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
        <DialogContent>
          <Stack direction={'column'} spacing={2} sx={{ py: '10px' }}>
            <Stack
              sx={{
                borderStyle: 'dotted',
                borderRadius: '5px',
                borderColor: '#808080d6',
              }}
            >
              <Button
                component="label"
                fullWidth
                title="You can upload max of 2 files"
                tabIndex={-1}
                startIcon={<CloudUpload />}
              >
                Click here to browse the files
                <VisuallyHiddenInput type="file" accept="image/*, .pdf" onChange={handleFileInputChange} />
              </Button>
            </Stack>
            <Stack>
              <div id="supporting-new-docuemnts-container">
                {files.length ? (
                  files.map((item) => {
                    return (
                      <Paper key={item.id}>
                        <Stack spacing={1} alignItems={'center'} sx={{ height: '100%' }}>
                          <Stack flexGrow={1} justifyContent={'center'} sx={{ width: '-webkit-fill-available' }}>
                            <Stack justifyContent={'center'} alignSelf={'center'}>
                              <img
                                style={{
                                  width: '100%',
                                  flexGrow: '1',
                                  height: '100px',
                                }}
                                src={item.base64Image}
                                alt={item.name}
                              ></img>
                            </Stack>
                          </Stack>
                          {AuthStore.userDetails.Role.toUpperCase() === 'SUAD' && (
                            <IconButton
                              sx={{
                                position: 'absolute',
                                marginLeft: '105px !important',
                                marginTop: '-15px !important',
                              }}
                              onClick={() => handleRemoveDocument(item.id)}
                            >
                              <SmallAvatar alt="Remy Sharp">
                                <DeleteForever sx={{ height: '18px', width: '18px' }}></DeleteForever>
                              </SmallAvatar>
                            </IconButton>
                          )}
                          <Typography
                            sx={{
                              fontWeight: '300',
                              fontSize: '12px',
                              p: '5px',
                            }}
                            component="p"
                          >
                            {item.name}
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })
                ) : (
                  <Typography
                    sx={{
                      fontWeight: '500',
                      alignSelf: 'center',
                      fontSize: '16px',
                    }}
                    component="p"
                  >
                    No documents available
                  </Typography>
                )}
              </div>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: '25px', pt: '0px', justifyContent: 'center' }}>
          <CustomUploadButton variant="contained" disabled={files.length === 0} type="submit" autoFocus>
            Upload files
          </CustomUploadButton>
        </DialogActions>
      </CustomStyledDialog>
    );
  }
);
